import { recordAccountSignup } from "../core/lib/analytics/account-signup"
import { useSaveIsAuthenticated } from "core/hooks/use-is-authenticated"
import { useMutation } from "@tanstack/react-query"
import { createSeamAxios } from "@seamapi/shared/seam-axios"
import type { AxiosError } from "axios"
import toast from "react-hot-toast"
import { useSignOut } from "./use-sign-out"
import posthog from "posthog-js"
import { useClerkCreatedAt } from "core/hooks/use-clerk-created-at"

interface CreateSessionParams {
  clerkToken: string
}

const unauthenticatedAxios = createSeamAxios({
  baseURL: process.env.NEXT_PUBLIC_API_HOST,
})

export const useLogin = () => {
  const saveIsAuthenticated = useSaveIsAuthenticated()
  const signOut = useSignOut()
  const { createdAt } = useClerkCreatedAt()
  return useMutation({
    mutationFn: async (params: CreateSessionParams) => {
      const res = await unauthenticatedAxios
        .post("/internal/sessions/create", { token: params.clerkToken })
        .catch((e: AxiosError<{ error: { message: string } }, any>) => ({
          error: e,
        }))
      if ("error" in res) {
        const seamHealthRes = await unauthenticatedAxios
          .get("/health")
          .catch(() => ({ data: { ok: false } }))

        if (!seamHealthRes.data.ok) {
          toast.error("Error creating session, Seam API is not responding")
        } else {
          console.log(
            "Failed to create session: ",
            res.error.toString(),
            res.error?.response?.data
          )
          toast.error(
            `Failed to create session! "${res.error?.response?.data?.error?.message}" Signing you out...`
          )
        }
        setTimeout(() => {
          signOut()
        }, 5000)

        throw res.error
      } else {
        // The API receipt confirms a NEW external DB user; older/existing
        // responses have no receipt. Persist it before navigation.
        recordAccountSignup(res.data.signup)
        saveIsAuthenticated(true, res?.data?.session_token)

        // PostHog: Identify user and capture sign-in event.
        // `created_at` is sourced from Clerk (the API session response doesn't
        // include it). Aurora's PostHog targeting needs it to gate users by
        // signup cohort once the flag's rollout phase flips.
        const userId = res?.data?.user_id
        const userEmail = res?.data?.email
        if (userId) {
          posthog.identify(userId, {
            email: userEmail,
            created_at: createdAt?.toISOString(),
          })
          posthog.capture("user_signed_in", {
            user_id: userId,
            email: userEmail,
          })
        }
      }
    },
  })
}
